/**
 * Predictive search: localized endpoint (/ar/search/suggest.json), escaped output, keyboard support,
 * combobox semantics, and bilingual collection titles ("English | Arabic") shown in the active language.
 */
(function () {
  'use strict';

  const esc = (v) => (window.MM ? window.MM.escapeHtml(v) : String(v));
  const money = (c) => (window.MM ? window.MM.formatMoney(c) : (c / 100).toFixed(2));
  const rtl = () => document.documentElement.dir === 'rtl';
  const localTitle = (title) => {
    const parts = String(title || '').split(' | ');
    if (parts.length < 2) return parts[0];
    return rtl() ? parts[parts.length - 1] : parts[0];
  };

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('.predictive-search-panel');
      this.timer = null;
      this.index = -1;
      this.controller = null;
      if (!this.input || !this.panel) return;

      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-autocomplete', 'list');
      if (this.panel.id) this.input.setAttribute('aria-controls', this.panel.id);

      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        const q = this.input.value.trim();
        if (q.length < 2) return this.close();
        this.timer = setTimeout(() => this.search(q), 250);
      });

      this.input.addEventListener('keydown', (e) => {
        if (!this.isOpen()) return;
        const items = this.panel.querySelectorAll('.predictive-search-item');
        if (!items.length) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.index = (this.index + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
          items.forEach((el, i) => { el.classList.toggle('bg-stone-100', i === this.index); el.setAttribute('aria-selected', String(i === this.index)); });
          items[this.index].scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter' && this.index >= 0) {
          e.preventDefault();
          items[this.index].click();
        } else if (e.key === 'Escape') {
          this.close();
        }
      });

      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
    }

    async search(q) {
      const url = (window.theme && window.theme.routes && window.theme.routes.predictiveSearchUrl) || '/search/suggest.json';
      this.controller && this.controller.abort();
      this.controller = new AbortController();
      this.panel.innerHTML = `<div class='p-4 text-center text-xs text-stone-500'>${rtl() ? 'جاري البحث...' : 'Searching...'}</div>`;
      this.open();
      try {
        const res = await fetch(url + '?q=' + encodeURIComponent(q) + '&resources[type]=product,collection&resources[limit]=6', { signal: this.controller.signal });
        const data = await res.json();
        const r = (data.resources && data.resources.results) || {};
        this.render(r.products || [], r.collections || [], q);
      } catch (err) {
        if (err.name !== 'AbortError') this.close();
      }
    }

    render(products, collections, q) {
      const ar = rtl();
      if (!products.length && !collections.length) {
        this.panel.innerHTML = `<div class='p-6 text-center text-xs text-stone-600'>${ar ? 'لا توجد نتائج مطابقة لـ ' : 'No results found for '}"${esc(q)}"</div>`;
        return this.open();
      }
      this.index = -1;
      let html = "<div class='p-2 space-y-1'>";
      if (collections.length) {
        html += `<div class='px-3 py-1.5 text-[11px] font-bold text-stone-600 uppercase tracking-wider'>${ar ? 'الأقسام' : 'Categories'}</div>`;
        collections.slice(0, 2).forEach((c) => {
          html += `<a href='${esc(c.url)}' role='option' class='predictive-search-item flex items-center gap-3 p-2.5 rounded-lg hover:bg-stone-100'><span class='text-xs font-bold text-stone-900'>${esc(localTitle(c.title))}</span></a>`;
        });
      }
      if (products.length) {
        html += `<div class='px-3 py-1.5 text-[11px] font-bold text-stone-600 uppercase tracking-wider'>${ar ? 'المنتجات' : 'Products'}</div>`;
        products.forEach((p) => {
          const img = (p.featured_image && p.featured_image.url) || p.image || '';
          const priceCents = Math.round(parseFloat(p.price) * 100);
          html += `<a href='${esc(p.url)}' role='option' class='predictive-search-item flex items-center gap-3 p-2.5 rounded-lg hover:bg-stone-100'>` +
            (img ? `<img src='${esc(img)}' width='40' height='40' loading='lazy' class='w-10 h-10 rounded-md object-cover flex-shrink-0' alt=''>` : '') +
            `<div class='flex-1 min-w-0'><h4 class='text-xs font-semibold text-stone-900 truncate'>${esc(p.title)}</h4>` +
            `<div class='text-xs font-bold text-emerald-800'>${isNaN(priceCents) ? '' : money(priceCents)}</div></div></a>`;
        });
      }
      const all = ((window.theme && window.theme.routes && window.theme.routes.searchUrl) || '/search') + '?q=' + encodeURIComponent(q);
      html += `<div class='pt-2 border-t border-stone-100 mt-1'><a href='${esc(all)}' class='block text-center text-xs font-bold text-emerald-800 py-2'>${ar ? 'عرض جميع النتائج' : 'View all results'} <span class='mm-arrow'>&rarr;</span></a></div></div>`;
      this.panel.innerHTML = html;
      this.open();
    }

    open() {
      this.panel.classList.remove('hidden');
      this.panel.style.display = 'block';
      this.input.setAttribute('aria-expanded', 'true');
    }
    close() {
      this.panel.classList.add('hidden');
      this.panel.style.display = 'none';
      this.index = -1;
      this.input.setAttribute('aria-expanded', 'false');
    }
    isOpen() { return !this.panel.classList.contains('hidden'); }
  }

  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
})();
